// Modules:
import React, { useState, useEffect, useCallback, useContext } from 'react';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';

// Utils:
import TopicConfigContext from 'utilities/topicConfigContext';

// Style:
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// Components:
import MobileDrawer from 'components/UI/MobileDrawer';
import LegendItems from './LegendItems';

const LegendList = ({
    layers,
    activeLayers,
    mapZoom,
    pathname,
    queryStringParse,
    routeToLayers,
    setMissingSuppressedModalOpen,
    styles,
    timeSliderMarks,
    setTimeSliderMarks,
}) => {
    // // State // //
    const [expanded, setExpanded] = useState('tableData');
    const [showDrawer, setShowDrawer] = useState(false);
    const [windowSize, setWindowSize] = useState(window.innerWidth);

    // // Context // //
    const { tab: currentTab } = useContext(TopicConfigContext);

    // // Methods // //
    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const determineParams = () => {
        const paramsObject = queryStringParse();

        const { jurisdiction } = paramsObject;

        if (jurisdiction) {
            return `?jurisdiction=${jurisdiction}&`;
        }
        return `?`;
    };

    const determineClassName = (isFixed, layerIsOn, type) => {
        if (isFixed) {
            return 'fixed-layer-plate';
        }
        if (layerIsOn) {
            return type === 'classBreaks'
                ? 'layer-plate selected-layer-plate'
                : 'min-content-layer-plate min-content-selected-layer-plate';
        }
        return type === 'classBreaks'
            ? 'layer-plate unselected-layer-plate'
            : 'min-content-layer-plate min-content-unselected-layer-plate';
    };

    // Conditionally display the container div for the simple legend items. Could be refactored/reused for other conditional displays like 'missing surpressed' or 'zoom info'.
    // const determineDisplay = (possibleLayers) => {
    //     if (
    //         !possibleLayers.filter(
    //             (layer) =>
    //                 layer.toggleable && layer.renderInfo?.type === 'classBreaks'
    //         ).length
    //     ) {
    //         return '-no-display';
    //     }
    //     return '';
    // };

    const handleShowDrawer = () => {
        setShowDrawer(true);
    };

    const handleDrawerClose = () => {
        setShowDrawer(false);
    };

    const handleWindowResize = useCallback(() => {
        setWindowSize(window.innerWidth);
    }, []);

    const getSubtitle = (layer) => {
        const subtitle = layer.subtitle || '';
        const yearText =
            layer.layerYear && !currentTab.timeSlider
                ? ` (${layer.layerYear})`
                : '';
        return `${subtitle}${yearText}`;
    };

    // // Effects // //
    // Grabs layerYear and creates timeSlider marks array of objects.
    useEffect(() => {
        if (timeSliderMarks === null && layers[0].layerYear !== undefined) {
            const marks = layers
                .map((layer) => ({
                    value: layer.layerYear,
                    label: layer.layerYear?.toString(),
                }))
                .sort((layerA, layerB) => layerA.value - layerB.value);

            setTimeSliderMarks([...marks]);
        }
    }, [layers, activeLayers, timeSliderMarks, setTimeSliderMarks]);

    // Handles window resizing conditionals and keeps current window width in state. Info on this method here: https://stackoverflow.com/questions/60911209/how-to-detect-the-window-width-change
    useEffect(() => {
        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [handleWindowResize]);

    // // Render // //
    return layers.length ? (
        <Accordion
            expanded={expanded === 'tableData'}
            onChange={handleChange('tableData')}
        >
            <div className="accordion-summary-container">
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="tableData-content"
                    id="tableData-header"
                    sx={styles}
                >
                    <Typography sx={styles}>
                        {expanded ? 'Hide legend' : 'Show legend'}
                    </Typography>
                </AccordionSummary>
            </div>
            <AccordionDetails sx={styles}>
                <div className="legend-list-container">
                    {layers
                        .filter((layer) => !layer.toggleable)
                        .map((layer) => (
                            <div key={layer.id} className="fixed-legend-item">
                                <div>
                                    <div className="fixed-layer-plate">
                                        <div className="layer-title">
                                            {layer.title}
                                        </div>
                                        <small className="layer-subtitle">
                                            {getSubtitle(layer)}
                                            {layer.scaleDependent &&
                                            mapZoom < layer.minScale ? (
                                                <small className="zoom-helper-text">
                                                    (Zoom in to view)
                                                </small>
                                            ) : null}
                                        </small>
                                        <div className="legend-items-container">
                                            {layer.renderInfo ? (
                                                <LegendItems
                                                    layer={layer}
                                                    layerOn
                                                    setMissingSuppressedModalOpen={
                                                        setMissingSuppressedModalOpen
                                                    }
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
                {layers.filter((layer) => layer.toggleable).length ? (
                    <div className="legend-reference-layer-container">
                        {/* Filtering for legend items with greater amounts of detail, usually with the type description: 'classBreaks' */}
                        <div className="legend-list-reference-container">
                            {/* Adjust style for mobile.  Move less detailed legend items into collapsible drawer 'layer manager'  */}
                            {windowSize <= 600 ? (
                                <>
                                    <div className="legend-drawer-button-container">
                                        <Button
                                            sx={styles}
                                            variant="contained"
                                            onClick={handleShowDrawer}
                                            className="legend-drawer-button"
                                        >
                                            Manage Additional Layers
                                        </Button>
                                    </div>
                                    <MobileDrawer
                                        showDrawer={showDrawer}
                                        handleDrawerClose={handleDrawerClose}
                                        layers={layers}
                                        activeLayers={activeLayers}
                                        pathname={pathname}
                                        determineClassName={determineClassName}
                                        determineParams={determineParams}
                                        routeToLayers={routeToLayers}
                                        mapZoom={mapZoom}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="legend-list-classbreaks-container">
                                        {/* Filter for reference layers with greater detail, such as multiple symbols, by renderInfo type */}
                                        {layers
                                            .filter(
                                                (layer) =>
                                                    layer.toggleable &&
                                                    layer.renderInfo?.type ===
                                                        'classBreaks'
                                            )
                                            .map((layer) => {
                                                const layerOn = activeLayers.includes(
                                                    layer.id
                                                );
                                                return (
                                                    <div
                                                        className="legend-list-item"
                                                        key={layer.id}
                                                    >
                                                        <Link
                                                            to={`${pathname}${determineParams()}layers=${routeToLayers(
                                                                layer.id,
                                                                layerOn
                                                            )}`}
                                                            value={layer.id}
                                                            key={layer.id}
                                                            className="toggle-card-links"
                                                        >
                                                            <div
                                                                className={determineClassName(
                                                                    !layer.toggleable,
                                                                    layerOn,
                                                                    layer
                                                                        .renderInfo
                                                                        ?.type
                                                                )}
                                                            >
                                                                <div className="detail-layer-title-and-content-main-container">
                                                                    <div className="detail-layer-title-and-eye-container">
                                                                        <div className="detail-eye-container">
                                                                            {activeLayers.includes(
                                                                                layer.id
                                                                            ) ? (
                                                                                <VisibilityIcon />
                                                                            ) : (
                                                                                <VisibilityOffIcon />
                                                                            )}
                                                                        </div>
                                                                        <div className="detail-layer-title">
                                                                            {
                                                                                layer.title
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                    <small className="detail-layer-subtitle">
                                                                        {getSubtitle(
                                                                            layer
                                                                        )}
                                                                    </small>
                                                                </div>
                                                                <div className="legend-items-container">
                                                                    {layer.renderInfo ? (
                                                                        <LegendItems
                                                                            layer={
                                                                                layer
                                                                            }
                                                                            layerOn={
                                                                                layerOn
                                                                            }
                                                                        />
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    </div>
                                                );
                                            })}
                                    </div>

                                    <div className="legend-list-checkbox-container">
                                        {layers
                                            .filter(
                                                (layer) =>
                                                    layer.toggleable &&
                                                    layer.renderInfo?.type !==
                                                        'classBreaks'
                                            )
                                            .map((layer) => {
                                                const layerOn = activeLayers.includes(
                                                    layer.id
                                                );
                                                return (
                                                    <div
                                                        className="legend-list-checkbox-item"
                                                        key={layer.id}
                                                    >
                                                        <Link
                                                            to={`${pathname}${determineParams()}layers=${routeToLayers(
                                                                layer.id,
                                                                layerOn
                                                            )}`}
                                                            value={layer.id}
                                                            key={layer.id}
                                                            className="toggle-card-links"
                                                        >
                                                            <div
                                                                className={determineClassName(
                                                                    !layer.toggleable,
                                                                    layerOn,
                                                                    layer
                                                                        .renderInfo
                                                                        ?.type
                                                                )}
                                                            >
                                                                <div className="min-content-icon-and-text-container">
                                                                    <div className="min-content-legend-items-container">
                                                                        {layer.renderInfo ? (
                                                                            <LegendItems
                                                                                layer={
                                                                                    layer
                                                                                }
                                                                                layerOn={
                                                                                    layerOn
                                                                                }
                                                                            />
                                                                        ) : null}
                                                                    </div>
                                                                    <div className="min-content-layer-details-container">
                                                                        <div className="min-content-layer-title">
                                                                            {
                                                                                layer.title
                                                                            }
                                                                        </div>
                                                                        <div className="min-content-layer-subtitle">
                                                                            {`${
                                                                                layer.subtitle
                                                                            } ${
                                                                                layer.layerYear
                                                                                    ? ` (${layer.layerYear})`
                                                                                    : ''
                                                                            }`}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="min-content-eye-container">
                                                                    {activeLayers.includes(
                                                                        layer.id
                                                                    ) ? (
                                                                        <VisibilityIcon />
                                                                    ) : (
                                                                        <VisibilityOffIcon />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ) : null}
                {/* TODO: Should the missing/suppressed legend footer note be dynamic and only appear when legend contains missing/suppressed data? */}
                <div className="info-modal-trigger-container">
                    <div
                        className="missing-suppressed-info-container"
                        onClick={() => setMissingSuppressedModalOpen(true)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                setMissingSuppressedModalOpen(true);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <p>*Missing or suppressed data?</p>
                        <Typography>
                            <InfoOutlinedIcon />
                        </Typography>
                    </div>
                </div>
            </AccordionDetails>
        </Accordion>
    ) : null;
};

export default LegendList;
